import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const DescriptionSection = ({ formData, onUpdate, errors = {} }) => {
  const [activeLanguage, setActiveLanguage] = useState('ar');
  const [activeDescriptionType, setActiveDescriptionType] = useState('description'); // 'description', 'card', 'about'
  const [contacts, setContacts] = useState(formData?.contacts || []);
  const [newContact, setNewContact] = useState({
    name: '',
    position: '',
    email: '',
    phone: '',
    isPrimary: false
  });
  const [activeContentType, setActiveContentType] = useState('specialAchievements'); // 'specialAchievements', 'activitiesAndAchievements', 'currentAdministrativeTeam', 'notes'

  // Sync contacts from formData
  useEffect(() => {
    if (formData?.contacts) {
      setContacts(formData.contacts);
    }
  }, [formData?.contacts]);

  const languages = [
    { code: 'ar', label: 'العربية', flag: '🇸🇾', dir: 'rtl' },
    { code: 'en', label: 'English', flag: '🇺🇸', dir: 'ltr' },
    { code: 'tr', label: 'Türkçe', flag: '🇹🇷', dir: 'ltr' }
  ];

  const handleDescriptionChange = (language, value, type = 'description') => {
    let fieldName;
    if (type === 'card') {
      fieldName = `cardDescription${language?.charAt(0)?.toUpperCase() + language?.slice(1)}`;
    } else if (type === 'about') {
      fieldName = `aboutDescription${language?.charAt(0)?.toUpperCase() + language?.slice(1)}`;
    } else {
      fieldName = `description${language?.charAt(0)?.toUpperCase() + language?.slice(1)}`;
    }
    onUpdate({ [fieldName]: value });
  };

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
  };

  const handleContactChange = (field, value) => {
    setNewContact(prev => ({ ...prev, [field]: value }));
  };

  const addContact = () => {
    if (newContact?.name && newContact?.email) {
      const updatedContacts = [...contacts, { ...newContact, id: Date.now() }];
      setContacts(updatedContacts);
      onUpdate({ contacts: updatedContacts });
      setNewContact({
        name: '',
        position: '',
        email: '',
        phone: '',
        isPrimary: false
      });
    }
  };

  const removeContact = (contactId) => {
    const updatedContacts = contacts?.filter(contact => contact?.id !== contactId);
    setContacts(updatedContacts);
    onUpdate({ contacts: updatedContacts });
  };

  const setPrimaryContact = (contactId) => {
    const updatedContacts = contacts?.map(contact => ({
      ...contact,
      isPrimary: contact?.id === contactId
    }));
    setContacts(updatedContacts);
    onUpdate({ contacts: updatedContacts });
  };

  const getCurrentContent = () => {
    const field = `${activeContentType}${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
    return formData?.[field] || '';
  };

  const handleContentChange = (language, value, type) => {
    const field = `${type}${language?.charAt(0)?.toUpperCase() + language?.slice(1)}`;
    onUpdate({ [field]: value });
  };

  const getContentPlaceholder = () => {
    const placeholders = {
      specialAchievements: {
        ar: 'أدخل الإنجازات الخاصة للاتحاد الفرعي...',
        en: 'Enter special achievements of the sub-union...',
        tr: 'Alt birliğin özel başarılarını girin...'
      },
      activitiesAndAchievements: {
        ar: 'أدخل الأنشطة والإنجازات...',
        en: 'Enter activities and achievements...',
        tr: 'Faaliyetler ve başarıları girin...'
      },
      currentAdministrativeTeam: {
        ar: 'أدخل معلومات الفريق الإداري الحالي...',
        en: 'Enter current administrative team information...',
        tr: 'Mevcut idari ekip bilgilerini girin...'
      },
      notes: {
        ar: 'أدخل ملاحظات إضافية...',
        en: 'Enter additional notes...',
        tr: 'Ek notlar girin...'
      }
    };
    return placeholders[activeContentType]?.[activeLanguage] || '';
  };

  const getContentLabel = () => {
    const labels = {
      specialAchievements: {
        ar: 'الإنجازات الخاصة',
        en: 'Special Achievements',
        tr: 'Özel Başarılar'
      },
      activitiesAndAchievements: {
        ar: 'الأنشطة والإنجازات',
        en: 'Activities and Achievements',
        tr: 'Faaliyetler ve Başarılar'
      },
      currentAdministrativeTeam: {
        ar: 'الفريق الإداري الحالي',
        en: 'Current Administrative Team',
        tr: 'Mevcut İdari Ekip'
      },
      notes: {
        ar: 'ملاحظات',
        en: 'Notes',
        tr: 'Notlar'
      }
    };
    return labels[activeContentType]?.[activeLanguage] || '';
  };

  const getCurrentDescription = () => {
    let field;
    if (activeDescriptionType === 'card') {
      field = `cardDescription${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
    } else if (activeDescriptionType === 'about') {
      field = `aboutDescription${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
    } else {
      field = `description${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
    }
    return formData?.[field] || '';
  };

  const getPlaceholder = () => {
    const typeLabels = {
      description: {
        ar: 'أدخل وصف مفصل للاتحاد الفرعي، أهدافه، أنشطته، وخدماته...',
        en: 'Enter a detailed description of the sub-union, its goals, activities, and services...',
        tr: 'Alt birliğin ayrıntılı açıklamasını, hedeflerini, faaliyetlerini ve hizmetlerini girin...'
      },
      card: {
        ar: 'أدخل وصف مختصر للبطاقة (يظهر في قوائم الاتحادات)...',
        en: 'Enter a short description for the card (shown in union lists)...',
        tr: 'Kart için kısa bir açıklama girin (birlik listelerinde gösterilir)...'
      },
      about: {
        ar: 'أدخل وصف تفصيلي عن الاتحاد الفرعي (صفحة من نحن)...',
        en: 'Enter a detailed description about the sub-union (About page)...',
        tr: 'Alt birlik hakkında ayrıntılı bir açıklama girin (Hakkında sayfası)...'
      }
    };
    return typeLabels[activeDescriptionType]?.[activeLanguage] || '';
  };

  const getCurrentLanguage = () => {
    return languages?.find(lang => lang?.code === activeLanguage);
  };

  const formatText = (action) => {
    const textarea = document.getElementById('description-editor');
    if (!textarea) return;

    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const selectedText = textarea?.value?.substring(start, end);
    const beforeText = textarea?.value?.substring(0, start);
    const afterText = textarea?.value?.substring(end);

    let newText = '';
    let newCursorPos = start;

    switch (action) {
      case 'bold':
        newText = `${beforeText}**${selectedText}**${afterText}`;
        newCursorPos = selectedText ? end + 4 : start + 2;
        break;
      case 'italic':
        newText = `${beforeText}_${selectedText}_${afterText}`;
        newCursorPos = selectedText ? end + 2 : start + 1;
        break;
      case 'list':
        const lines = selectedText?.split('\n');
        const listItems = lines?.map(line => `• ${line}`)?.join('\n');
        newText = `${beforeText}${listItems}${afterText}`;
        newCursorPos = end + (lines?.length * 2);
        break;
      case 'link':
        const url = prompt('أدخل الرابط:');
        if (url) {
          newText = `${beforeText}[${selectedText || 'نص الرابط'}](${url})${afterText}`;
          newCursorPos = end + url?.length + 4;
        } else {
          return;
        }
        break;
      default:
        return;
    }

    handleDescriptionChange(activeLanguage, newText, activeDescriptionType);
    
    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center space-x-3 pb-4 border-b border-border">
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
          <Icon name="FileText" size={18} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground font-arabic">
            الوصف والمحتوى
          </h3>
          <p className="text-sm text-muted-foreground font-caption">
            أضف وصف مفصل للاتحاد الفرعي بلغات متعددة
          </p>
        </div>
      </div>
      
      {/* Description Type Selector */}
      <div className="flex items-center space-x-2 bg-muted rounded-lg p-1">
        <Button
          variant={activeDescriptionType === 'description' ? 'default' : 'ghost'}
          size="sm"
          className="flex-1"
          onClick={() => setActiveDescriptionType('description')}
        >
          الوصف العام
        </Button>
        <Button
          variant={activeDescriptionType === 'card' ? 'default' : 'ghost'}
          size="sm"
          className="flex-1"
          onClick={() => setActiveDescriptionType('card')}
        >
          وصف البطاقة
        </Button>
        <Button
          variant={activeDescriptionType === 'about' ? 'default' : 'ghost'}
          size="sm"
          className="flex-1"
          onClick={() => setActiveDescriptionType('about')}
        >
          صفحة من نحن
        </Button>
      </div>

      {/* Language Tabs */}
      <div className="flex items-center space-x-2 bg-muted rounded-lg p-1">
        {languages?.map((language) => (
          <Button
            key={language?.code}
            variant={activeLanguage === language?.code ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveLanguage(language?.code)}
          >
            <span className="mr-2">{language?.flag}</span>
            {language?.label}
          </Button>
        ))}
      </div>
      {/* Editor Toolbar */}
      <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg border border-border">
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => formatText('bold')}
            iconName="Bold"
            iconSize={16}
            title="عريض"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => formatText('italic')}
            iconName="Italic"
            iconSize={16}
            title="مائل"
          />
          <div className="w-px h-6 bg-border mx-2" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => formatText('list')}
            iconName="List"
            iconSize={16}
            title="قائمة"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => formatText('link')}
            iconName="Link"
            iconSize={16}
            title="رابط"
          />
        </div>

        <div className="flex-1" />

        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Globe" size={16} />
          <span className="font-caption">
            {getCurrentLanguage()?.label}
          </span>
        </div>
      </div>
      {/* Text Editor */}
      <div className="space-y-2">
        <div className="relative">
          <textarea
            id="description-editor"
            className={`w-full h-64 p-4 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
              getCurrentLanguage()?.dir === 'rtl' ? 'text-right' : 'text-left'
            }`}
            dir={getCurrentLanguage()?.dir}
            placeholder={getPlaceholder()}
            value={getCurrentDescription()}
            onChange={(e) => handleDescriptionChange(activeLanguage, e?.target?.value, activeDescriptionType)}
          />
          
          {/* Character Count */}
          <div className="absolute bottom-3 left-3 text-xs text-muted-foreground bg-background px-2 py-1 rounded">
            {getCurrentDescription()?.length} حرف
          </div>
        </div>

        {(() => {
          let errorField;
          if (activeDescriptionType === 'card') {
            errorField = `cardDescription${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
          } else if (activeDescriptionType === 'about') {
            errorField = `aboutDescription${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
          } else {
            errorField = `description${activeLanguage?.charAt(0)?.toUpperCase() + activeLanguage?.slice(1)}`;
          }
          return errors?.[errorField] && (
            <p className="text-sm text-error font-caption">
              {errors?.[errorField]}
            </p>
          );
        })()}
      </div>
      {/* Content Guidelines */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Lightbulb" size={16} className="text-accent mt-0.5" />
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-accent font-arabic">
              نصائح لكتابة وصف فعال
            </h4>
            <ul className="text-xs text-accent/80 space-y-1 font-caption">
              <li>• اذكر الأهداف الرئيسية للاتحاد الفرعي</li>
              <li>• وضح الخدمات والأنشطة المقدمة</li>
              <li>• أضف معلومات الاتصال والموقع</li>
              <li>• استخدم لغة واضحة ومفهومة</li>
              <li>• تأكد من ترجمة المحتوى لجميع اللغات</li>
            </ul>
          </div>
        </div>
      </div>
      {/* Preview Section */}
      <div className="border border-border rounded-lg p-4 bg-card">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-foreground font-arabic">
            معاينة المحتوى
          </h4>
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <span className="font-caption">{getCurrentLanguage()?.label}</span>
            <span>{getCurrentLanguage()?.flag}</span>
          </div>
        </div>
        
        <div 
          className={`prose prose-sm max-w-none text-foreground ${
            getCurrentLanguage()?.dir === 'rtl' ? 'text-right' : 'text-left'
          }`}
          dir={getCurrentLanguage()?.dir}
        >
          {getCurrentDescription() ? (
            <div className="whitespace-pre-wrap">
              {getCurrentDescription()}
            </div>
          ) : (
            <p className="text-muted-foreground italic font-caption">
              لا يوجد محتوى للمعاينة...
            </p>
          )}
        </div>
      </div>

      {/* Member Statistics Section */}
      <div className="space-y-6 pt-6 border-t border-border">
        <div className="flex items-center space-x-3 pb-4">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Users" size={18} color="white" />
          </div>
          <div>
            <h4 className="text-md font-semibold text-foreground font-arabic">
              إدارة الأعضاء
            </h4>
            <p className="text-sm text-muted-foreground font-caption">
              إدارة معلومات الأعضاء
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="UserCheck" size={20} className="text-success" />
              </div>
              <div>
                <Input
                  label="العدد الإجمالي للأعضاء"
                  type="number"
                  placeholder="0"
                  value={formData?.totalMembers || ''}
                  onChange={(e) => handleInputChange('totalMembers', e?.target?.value)}
                  error={errors?.totalMembers}
                  min="0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Content Sections */}
      <div className="space-y-6 pt-6 border-t border-border">
        <div className="flex items-center space-x-3 pb-4">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={18} color="white" />
          </div>
          <div>
            <h4 className="text-md font-semibold text-foreground font-arabic">
              محتوى إضافي
            </h4>
            <p className="text-sm text-muted-foreground font-caption">
              أضف معلومات إضافية عن الاتحاد الفرعي
            </p>
          </div>
        </div>

        {/* Content Type Selector */}
        <div className="flex items-center space-x-2 bg-muted rounded-lg p-1">
          <Button
            variant={activeContentType === 'specialAchievements' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveContentType('specialAchievements')}
          >
            الإنجازات الخاصة
          </Button>
          <Button
            variant={activeContentType === 'activitiesAndAchievements' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveContentType('activitiesAndAchievements')}
          >
            الأنشطة والإنجازات
          </Button>
          <Button
            variant={activeContentType === 'currentAdministrativeTeam' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveContentType('currentAdministrativeTeam')}
          >
            الفريق الإداري
          </Button>
          <Button
            variant={activeContentType === 'notes' ? 'default' : 'ghost'}
            size="sm"
            className="flex-1"
            onClick={() => setActiveContentType('notes')}
          >
            ملاحظات
          </Button>
        </div>

        {/* Language Tabs for Additional Content */}
        <div className="flex items-center space-x-2 bg-muted rounded-lg p-1">
          {languages?.map((language) => (
            <Button
              key={language?.code}
              variant={activeLanguage === language?.code ? 'default' : 'ghost'}
              size="sm"
              className="flex-1"
              onClick={() => setActiveLanguage(language?.code)}
            >
              <span className="mr-2">{language?.flag}</span>
              {language?.label}
            </Button>
          ))}
        </div>

        {/* Content Editor */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground font-arabic">
            {getContentLabel()}
          </label>
          <textarea
            className={`w-full h-48 p-4 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
              languages?.find(l => l?.code === activeLanguage)?.dir === 'rtl' ? 'text-right' : 'text-left'
            }`}
            dir={languages?.find(l => l?.code === activeLanguage)?.dir}
            placeholder={getContentPlaceholder()}
            value={getCurrentContent()}
            onChange={(e) => handleContentChange(activeLanguage, e?.target?.value, activeContentType)}
          />
        </div>
      </div>
    </div>
  );
};

export default DescriptionSection;